import { motion } from "framer-motion";
import { ArrowUpRight, FileSignature, GraduationCap, ShieldCheck } from "lucide-react";

const PILLARS = [
  {
    icon: FileSignature,
    name: "Counseling & Documentation",
    desc: "COACH drafts behavior-based write-ups, coaching plans, and termination frameworks — signed PDFs in seconds.",
  },
  {
    icon: GraduationCap,
    name: "Training & Certification",
    desc: "Role-based courses, gated final exams, and verifiable wall-ready certificates with a public registry.",
  },
  {
    icon: ShieldCheck,
    name: "Compliance Guardrails",
    desc: "Fair-housing-safe language and correct documentation order baked into every people workflow.",
  },
];

export const HrBrand = () => (
  <section className="relative py-24 sm:py-32 border-t border-slate-100" data-testid="hr-brand-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="technical-card p-8 sm:p-12 flex flex-col items-center text-center"
          data-testid="hr-brand-card"
        >
          <img
            src="/hr-logo-mark.png"
            alt="OPERA360 HR logo"
            className="h-24 w-24 rounded-full shadow-[0_0_60px_rgba(46,156,255,0.25)]"
            data-testid="hr-brand-logo"
          />
          <p className="mt-6 font-cabinet text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            OPERA<span className="text-sky-600">360</span> <span className="font-editorial italic font-normal text-green-700">HR</span>
          </p>
          <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
            The people layer of the portfolio
          </p>
          <p className="mt-5 text-sm text-muted-foreground leading-relaxed max-w-sm">
            Counseling, coaching, certification, and compliance for the teams who run your communities —
            part of the Christopher Love platform family with OPERA360 and ScopeOne Walk.
          </p>
          <a
            href="https://www.opera360hr.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hr-brand-link"
            className="mt-7 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-sky-600 hover:text-cadmium-hover transition-colors"
          >
            WWW.OPERA360HR.COM <ArrowUpRight size={12} />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="eyebrow mb-4 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-cadmium" /> Brand Family
          </p>
          <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
            Your HR department,{" "}
            <span className="font-editorial italic font-normal text-sky-600">built in.</span>
          </h2>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            OPERA360 HR is the people-operations brand of the family — made for community managers and
            regional leaders who handle coaching, documentation, and hard conversations without an HR
            department on call. Every people decision comes out consistent, compliant, and defensible.
          </p>
          <div className="mt-8 space-y-5" data-testid="hr-brand-pillars">
            {PILLARS.map((p) => (
              <div key={p.name} className="flex items-start gap-4">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-cadmium/40 bg-cadmium/10 text-sky-600">
                  <p.icon size={16} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
